import React from 'react';
import { useMouseScrubVideo } from './useMouseScrubVideo';

/** The plate the site closes on, so every ending shares one frame. */
/*
 * Hosted, not a project-relative path. The previous value was a bare filename
 * at the site root, which resolves to a 404 wherever the file is not sitting
 * beside index.html — so the plate silently painted nothing and every closing
 * panel fell back to flat ink.
 */
export const CLOSING_VIDEO = 'https://cdn.magicpatterns.com/uploads/1x73Uk4K7Wa9yMnzC7U5iw/hf_20260619_223334_50388977-efc1-401a-95ea-f11fa2ea2644 (1).mp4';
export const CLOSING_POSTER = 'https://mirrorful-production.s3.us-west-1.amazonaws.com/patterns/assets/c9cswf4udjck7zyoebqqwd/63a18a4c-5d20-4ad2-ba5f-9334c775fe47/Untitled_design_(6)_1.webp';

/**
 * The mouse-scrubbed video plate that sits behind a dark closing panel.
 *
 * It existed only inside ClosingCTA, so it appeared on the older home variants
 * and nowhere else. Every panel rebuilt since — pricing, the feature pages,
 * the enterprise platform page — ends on the same ink ground with nothing
 * behind it, which is why the one genuinely tactile moment on the site was
 * almost impossible to find.
 *
 * Notes that matter if this is ever touched:
 *
 * - The video NEVER autoplays; the hook drives currentTime from horizontal
 *   mouse movement. `poster` is therefore not optional — without it the
 *   element paints nothing until the visitor happens to move the cursor.
 * - `preload` MUST be "metadata". "none" loads no duration, and the hook needs
 *   `video.duration` to seek, so scrubbing would be dead AND the frame blank.
 *   "metadata" is a few KB, so the saving still holds.
 * - Desktop only. There is no cursor on a phone, so the plate would be a
 *   silent multi-MB download for an interaction that cannot happen.
 */
export function ScrubVideoPlate({
  src,
  poster,
  /** object-position for the crop, e.g. '78% top'. */
  position = '78% top',
  /** Ink used for the scrim so the plate matches the panel it sits in. */
  ground = '#1A120C'
}: {
  src: string;
  poster: string;
  position?: string;
  ground?: string;
}) {
  const videoRef = useMouseScrubVideo<HTMLVideoElement>();
  return <div aria-hidden="true" className="pointer-events-none absolute inset-0 hidden md:block" data-id="element-5294" data-mp="2pghkucj9iekj">
      <video ref={videoRef} src={src} poster={poster} muted playsInline preload="metadata" className="size-full object-cover" style={{
      objectPosition: position,
      backgroundColor: ground
    }} data-id="element-5295" data-mp="2khwk8c03lsvk" />
      {/* The copy sits on the left, so the plate is held back hardest there and
       released across the right half where the figure lives. A flat scrim over
       the whole frame would have dimmed the part worth looking at. */}
      <div className="absolute inset-0" style={{
      background: `linear-gradient(to right, ${ground} 0%, ${ground}F2 34%, ${ground}99 58%, ${ground}40 100%)`
    }} data-id="element-5296" data-mp="21qdie8ht8kv4" />
    </div>;
}